import { MinorBreakRule } from 'constants/MinorBreakRule';
import { TFunction } from 'i18next';
import {
  CONTRACT_COMPLIANCE__BREAK_MAXIMUM,
  CONTRACT_COMPLIANCE__BREAK_RANGE,
  CONTRACT_COMPLIANCE__CONTINUOUS_EXCEEDS_TOTAL_HINT,
  CONTRACT_COMPLIANCE__DEFAULT_CONTINUOUS_WORK,
  CONTRACT_COMPLIANCE__DEFAULT_LEGAL_WORK_TIME,
  CONTRACT_COMPLIANCE__REQUIRED_WHOLE_MINUTES,
  CONTRACT_COMPLIANCE__THRESHOLD_POSITIVE,
} from 'translations/constants';
import {
  IJustificationPayload,
  IMinorBreakRuleDefault,
  ISaveMinorBreakDefault,
} from 'types/contract-compliance/IContractCompliance';
import { formatHoursMinutes, formatMinutes, parseMinutesInput } from '../utils';

/** Strings, so an input the user clears stays empty instead of turning into 0. */
export type DefaultDraft = {
  threshold: string;
  minimumBreak: string;
  continuousBreak: string;
};

const MAX_BREAK_MINUTES = 60;

export type DefaultDraftErrors = Partial<Record<keyof DefaultDraft, string>>;

export const toDefaultDraft = (value: IMinorBreakRuleDefault): DefaultDraft => ({
  threshold: String(value.thresholdMinutes),
  minimumBreak: String(value.minimumBreakMinutes),
  continuousBreak: value.continuousBreakMinutes === null ? '' : String(value.continuousBreakMinutes),
});

/** Mirrors the server rules (backend note); 0 is a valid break and needs no justification. Returns translation keys. */
export const validateDefaultDraft = (rule: MinorBreakRule, draft: DefaultDraft): DefaultDraftErrors => {
  const errors: DefaultDraftErrors = {};
  const threshold = parseMinutesInput(draft.threshold);
  const minimum = parseMinutesInput(draft.minimumBreak);

  if (threshold === null) errors.threshold = CONTRACT_COMPLIANCE__REQUIRED_WHOLE_MINUTES;
  else if (threshold <= 0) errors.threshold = CONTRACT_COMPLIANCE__THRESHOLD_POSITIVE;

  if (minimum === null) errors.minimumBreak = CONTRACT_COMPLIANCE__REQUIRED_WHOLE_MINUTES;
  else if (threshold !== null && minimum > threshold) errors.minimumBreak = CONTRACT_COMPLIANCE__BREAK_RANGE;
  else if (minimum > MAX_BREAK_MINUTES) errors.minimumBreak = CONTRACT_COMPLIANCE__BREAK_MAXIMUM;

  if (rule === MinorBreakRule.LEGAL_WORK_TIME) {
    const continuous = parseMinutesInput(draft.continuousBreak);
    if (continuous === null) errors.continuousBreak = CONTRACT_COMPLIANCE__REQUIRED_WHOLE_MINUTES;
    else if (threshold !== null && continuous > threshold) errors.continuousBreak = CONTRACT_COMPLIANCE__BREAK_RANGE;
    else if (continuous > MAX_BREAK_MINUTES) errors.continuousBreak = CONTRACT_COMPLIANCE__BREAK_MAXIMUM;
    else if (minimum !== null && continuous > minimum) {
      errors.continuousBreak = CONTRACT_COMPLIANCE__CONTINUOUS_EXCEEDS_TOTAL_HINT;
    }
  }
  return errors;
};

export const isDefaultDraftChanged = (loaded: IMinorBreakRuleDefault, draft: DefaultDraft): boolean => {
  const original = toDefaultDraft(loaded);
  return (
    original.threshold !== draft.threshold.trim() ||
    original.minimumBreak !== draft.minimumBreak.trim() ||
    (loaded.rule === MinorBreakRule.LEGAL_WORK_TIME && original.continuousBreak !== draft.continuousBreak.trim())
  );
};

/** Rule 1 must send continuousBreakMinutes: null; the API rejects a value. */
export const toSaveDefaultBody = (
  rule: MinorBreakRule,
  draft: DefaultDraft,
  rowVersion: string,
  justification: IJustificationPayload,
): ISaveMinorBreakDefault => ({
  thresholdMinutes: Number(draft.threshold),
  minimumBreakMinutes: Number(draft.minimumBreak),
  continuousBreakMinutes: rule === MinorBreakRule.LEGAL_WORK_TIME ? Number(draft.continuousBreak) : null,
  rowVersion,
  ...justification,
});

/** "30 min after 4h30" / "60 min total (30 min continuous) after 6h00". */
export const formatRuleDefault = (t: TFunction, value: IMinorBreakRuleDefault): string =>
  value.rule === MinorBreakRule.LEGAL_WORK_TIME
    ? t(CONTRACT_COMPLIANCE__DEFAULT_LEGAL_WORK_TIME, {
        total: formatMinutes(t, value.minimumBreakMinutes),
        continuous: formatMinutes(t, value.continuousBreakMinutes ?? 0),
        threshold: formatHoursMinutes(value.thresholdMinutes),
      })
    : t(CONTRACT_COMPLIANCE__DEFAULT_CONTINUOUS_WORK, {
        break: formatMinutes(t, value.minimumBreakMinutes),
        threshold: formatHoursMinutes(value.thresholdMinutes),
      });
